// src/components/layout/AppLayout.tsx
import React from 'react';
import {
  AppShell,
  Text,
  Group,
  Button,
  Stack,
  Badge,
  Divider,
  ActionIcon,
  Menu,
  Avatar,
  UnstyledButton,
  Box,
  Tooltip,
  Burger,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import {
  IconLogout,
  IconUser,
  IconSettings,
  IconDashboard,
  IconClipboardList,
  IconUsers,
  IconPalette,
  IconBuildingFactory2,
  IconChevronDown,
  IconChartBar,
  IconLayoutKanban,
  IconHistory,
  IconChevronLeft,
  IconChevronRight,
  IconFlask,
} from '@tabler/icons-react';
import { Link, useLocation, useNavigate } from '@tanstack/react-router';
import { useSidebarCollapsed } from '../../hooks/use-sidebar-collapsed';
import { UserRole } from '../../types/api';
import { useOktaAuth } from '../../providers/okta-auth-provider';
import { NotificationBadge } from '../notifications/NotificationBadge';
import { NotificationCenter } from '../notifications/NotificationCenter';
import { useNotificationCenter } from '../../hooks/useNotificationCenter';

const NAVIGATION_ITEMS = [
  {
    path: '/',
    label: 'Dashboard',
    icon: IconDashboard,
    requiredRoles: [], // Tutti
  },
  {
    path: '/board',
    label: 'Kanban Board',
    icon: IconLayoutKanban,
    requiredRoles: [], // Tutti possono vedere il Kanban
  },
  {
    path: '/bozzetti',
    label: 'Bozzetti',
    icon: IconPalette,
    requiredRoles: [UserRole.Admin, UserRole.CapoUfficioStile, UserRole.UfficioStile],
  },
  {
    path: '/pre-campionatura',
    label: 'Pre-Campionatura',
    icon: IconFlask,
    requiredRoles: [UserRole.Admin, UserRole.CapoUfficioStile, UserRole.UfficioStile],
  },
  {
    path: '/campioni',
    label: 'Campioni',
    icon: IconBuildingFactory2,
    requiredRoles: [UserRole.Admin, UserRole.CapoIndustrializzazione, UserRole.Industrializzazione],
  },
  {
    path: '/produzione',
    label: 'Produzione',
    icon: IconSettings,
    requiredRoles: [UserRole.Admin, UserRole.CapoIndustrializzazione, UserRole.Industrializzazione],
  },
  {
    path: '/gamma',
    label: 'Gamma',
    icon: IconPalette,
    requiredRoles: [], // Tutti possono vedere Gamma
  },
  {
    path: '/storico',
    label: 'Storico',
    icon: IconHistory,
    requiredRoles: [], // Tutti possono vedere lo storico
  },
  {
    path: '/admin/utenti',
    label: 'Gestione Utenti',
    icon: IconUsers,
    requiredRoles: [UserRole.Admin],
  },
  {
    path: '/analytics',
    label: 'Analytics',
    icon: IconChartBar,
    requiredRoles: [UserRole.Admin, UserRole.CapoUfficioStile, UserRole.CapoIndustrializzazione],
  },
];

interface AppLayoutProps {
  children: React.ReactNode;
}

export function AppLayout({ children }: AppLayoutProps) {
  const { userInfo, logout, hasRole } = useOktaAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const { isCollapsed, toggleCollapsed } = useSidebarCollapsed();
  const [mobileOpened, { toggle: toggleMobile }] = useDisclosure();
  const { 
    opened: notificationCenterOpened, 
    toggleNotificationCenter, 
    closeNotificationCenter 
  } = useNotificationCenter();

  if (!userInfo) {
    return null;
  }

  const handleLogout = () => {
    logout();
  };

  const canAccessRoute = (requiredRoles: UserRole[]) => {
    if (requiredRoles.length === 0) return true;
    return hasRole(requiredRoles);
  };

  // Ottieni il ruolo principale dell'utente (il primo o il più alto in priorità)
  const getPrimaryRole = (): UserRole => {
    if (!userInfo?.roles || userInfo.roles.length === 0) return UserRole.OperatoreUfficioStile;
    // Priorità: Admin > AdminUfficioStile > OperatoreUfficioStile > AdminSviluppo > OperatoreSviluppo
    const priority = [
      UserRole.Admin,
      UserRole.AdminUfficioStile,
      UserRole.OperatoreUfficioStile,
      UserRole.AdminSviluppo,
      UserRole.OperatoreSviluppo,
      UserRole.User
    ];
    for (const role of priority) {
      if (userInfo.roles.includes(role)) return role;
    }
    return userInfo.roles[0];
  };

  const getUserDisplayName = (): string => {
    return userInfo.displayName || `${userInfo.firstName} ${userInfo.lastName}` || userInfo.email;
  };

  const getRoleName = (role: UserRole): string => {
    const roleNames = {
      [UserRole.Admin]: 'Amministratore',
      [UserRole.AdminUfficioStile]: 'Capo Ufficio Stile',
      [UserRole.OperatoreUfficioStile]: 'Ufficio Stile',
      [UserRole.AdminSviluppo]: 'Capo Industrializzazione',
      [UserRole.OperatoreSviluppo]: 'Industrializzazione',
      [UserRole.User]: 'Utente',
    };
    return roleNames[role] || 'Ruolo Sconosciuto';
  };

  const getRoleColor = (role: UserRole): string => {
    const roleColors = {
      [UserRole.Admin]: 'red',
      [UserRole.AdminUfficioStile]: 'blue',
      [UserRole.OperatoreUfficioStile]: 'cyan',
      [UserRole.AdminSviluppo]: 'orange',
      [UserRole.OperatoreSviluppo]: 'yellow',
      [UserRole.User]: 'gray',
    };
    return roleColors[role] || 'gray';
  };

  return (
    <AppShell
      header={{ height: 60 }}
      navbar={{ width: isCollapsed ? 80 : 280, breakpoint: 'sm', collapsed: { mobile: !mobileOpened } }}
      padding="md"
    >
      {/* Header */}
      <AppShell.Header>
        <Group h="100%" px="md" justify="space-between">
          <Group>
            <Burger opened={mobileOpened} onClick={toggleMobile} hiddenFrom="sm" size="sm" />
            <Text size="lg" fw={600}>Ufficio Stile</Text>
          </Group>

          <Group>
            <NotificationBadge 
              onClick={toggleNotificationCenter}
              size="md"
              variant="subtle"
            />
            
            <Menu shadow="md" width={200}>
            <Menu.Target>
              <UnstyledButton>
                <Group gap="sm">
                  <Avatar size="sm" radius="xl">
                    <IconUser size="1rem" />
                  </Avatar>
                  <Box style={{ flex: 1 }} visibleFrom="sm">
                    <Text size="sm" fw={500}>{getUserDisplayName()}</Text>
                    <Badge
                      color={getRoleColor(getPrimaryRole())}
                      variant="light"
                      size="xs"
                    >
                      {getRoleName(getPrimaryRole())}
                    </Badge>
                  </Box>
                  <IconChevronDown size="1rem" />
                </Group>
              </UnstyledButton>
            </Menu.Target>

            <Menu.Dropdown>
              <Menu.Label>Account</Menu.Label>
              <Menu.Item
                leftSection={<IconUser size="0.9rem" />}
                onClick={() => navigate({ to: '/profile' })}
              >
                Profilo
              </Menu.Item>
              <Menu.Item
                leftSection={<IconSettings size="0.9rem" />}
                onClick={() => navigate({ to: '/settings' })}
              >
                Impostazioni
              </Menu.Item>

              <Menu.Divider />

              <Menu.Item
                leftSection={<IconLogout size="0.9rem" />}
                color="red"
                onClick={handleLogout}
              >
                Logout
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>
          </Group>
        </Group>
      </AppShell.Header>

      {/* Navbar */}
      <AppShell.Navbar p={isCollapsed ? 'xs' : 'md'}>
        <Stack gap="xs" style={{ flex: 1 }}>
          {!isCollapsed && (
            <Text size="sm" c="dimmed" fw={500} mb="sm">
              Navigazione
            </Text>
          )}

          {NAVIGATION_ITEMS.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            const hasAccess = canAccessRoute(item.requiredRoles);

            if (!hasAccess) {
              return null;
            }

            return (
              <Tooltip key={item.path} label={item.label} position="right" disabled={!isCollapsed}>
                <Link
                  to={item.path}
                  style={{ textDecoration: 'none' }}
                  onClick={() => { if (mobileOpened) toggleMobile(); }}
                >
                  <UnstyledButton
                    w="100%"
                    p="sm"
                    style={{
                      borderRadius: '8px',
                      backgroundColor: isActive ? 'var(--mantine-color-blue-light)' : 'transparent',
                      color: isActive ? 'var(--mantine-color-blue-7)' : undefined,
                      border: isActive ? '1px solid var(--mantine-color-blue-3)' : '1px solid transparent',
                      display: 'flex',
                      justifyContent: isCollapsed ? 'center' : 'flex-start',
                    }}
                  >
                    {isCollapsed ? (
                      <Icon size="1.2rem" />
                    ) : (
                      <Group gap="sm">
                        <Icon size="1.2rem" />
                        <Text size="sm" fw={isActive ? 600 : 400}>
                          {item.label}
                        </Text>
                      </Group>
                    )}
                  </UnstyledButton>
                </Link>
              </Tooltip>
            );
          })}

          <Divider my="md" />

          {/* User Info Card - hidden when collapsed */}
          {!isCollapsed && (
            <Box
              p="sm"
              style={{
                borderRadius: '8px',
                backgroundColor: 'var(--mantine-color-gray-0)',
                border: '1px solid var(--mantine-color-gray-3)',
              }}
            >
              <Stack gap="xs">
                <Text size="xs" c="dimmed" fw={500}>
                  Utente Corrente
                </Text>
                <Group gap="sm">
                  <Avatar size="sm">
                    <IconUser size="1rem" />
                  </Avatar>
                  <Stack gap="2px" style={{ flex: 1 }}>
                    <Text size="sm" fw={500} lineClamp={1}>
                      {getUserDisplayName()}
                    </Text>
                    <Badge
                      color={getRoleColor(getPrimaryRole())}
                      variant="light"
                      size="xs"
                    >
                      {getRoleName(getPrimaryRole())}
                    </Badge>
                  </Stack>
                </Group>
              </Stack>
            </Box>
          )}

          {/* Toggle button */}
          <Box style={{ marginTop: 'auto' }}>
            <UnstyledButton
              w="100%"
              p="sm"
              onClick={toggleCollapsed}
              style={{
                borderRadius: '8px',
                border: '1px solid var(--mantine-color-gray-3)',
                display: 'flex',
                justifyContent: isCollapsed ? 'center' : 'flex-end',
              }}
            >
              {isCollapsed ? <IconChevronRight size="1rem" /> : <IconChevronLeft size="1rem" />}
            </UnstyledButton>
          </Box>
        </Stack>
      </AppShell.Navbar>

      {/* Main Content */}
      <AppShell.Main>
        {children}
      </AppShell.Main>
      
      {/* Notification Center */}
      <NotificationCenter
        opened={notificationCenterOpened}
        onClose={closeNotificationCenter}
      />
    </AppShell>
  );
}

export default AppLayout;